/**
 * "Chase the unredeemed invites" — Easy button #3
 * (memory-bank/plans/easy-buttons.md §3).
 *
 * A week after a launch, some of the fifteen accounts nobody claimed. There is
 * no mailer, so nothing chased them automatically, and finding out which ones
 * means reading the Users table and pressing the per-row invite button one
 * account at a time.
 *
 * The warning is the feature, not decoration
 * ------------------------------------------
 * Re-issuing an invite REVOKES the account's outstanding link. That is right
 * for a deliberate one-at-a-time re-issue — a link that leaked into a group
 * chat dies the moment a new one is sent — and it is a trap in a batch: a
 * manager who hand-delivered link A on Monday and pressed this on Tuesday has
 * turned link A into a dead link and will not find out until somebody
 * complains. There is no send log to check afterwards.
 *
 * So the server's default population is *never signed in AND link already
 * dead*, and reaching a live link takes the checkbox below. That checkbox says
 * what it does in the sentence next to it, not only in a docstring — the plan
 * is explicit that this has to be said in the UI.
 *
 * Everything else here follows the other Easy buttons: `ProposalLedger` for
 * the exclusion accounting (invariant I3 — a small number is nearly always the
 * data talking, and the ledger is how a manager can tell), untickable rows, and
 * a preview that is gated exactly like the commit.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, MailWarning, X } from 'lucide-react';

import ProposalLedger from '@/components/easy/ProposalLedger';
import { easyButtonError } from '@/api/easy';
import {
  bulkReissueInvites,
  fetchPendingInvites,
  type BulkInviteResponse,
  type PendingInviteItem,
  type PendingInvitesResponse,
} from '@/api/adminUsers';
import InviteLinkList, { InviteCsvButton } from '@/components/admin/InviteLinkList';

const n = (v: number) => v.toLocaleString();

function isAbortError(err: any): boolean {
  return (
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    err?.code === 'ERR_CANCELED'
  );
}

/**
 * `easyButtonError` for the shape, with a fallback that fits this door.
 *
 * The shared helper's generic 403 line says "requires a coordinator or above",
 * which is the ceiling for the routes and shifts buttons and the wrong one
 * here — provisioning is a per-account capability, not a rank. The server
 * almost always sends a `detail` and that wins; this only covers the case
 * where it does not.
 */
function chaseError(err: any, fallback: string): string {
  if (err?.response?.status === 403 && !err?.response?.data?.detail) {
    return (
      'You are not permitted to issue invites. That needs an administrator, ' +
      'or an account that has been granted provisioning rights.'
    );
  }
  return easyButtonError(err, fallback);
}

function waited(days: number | null): string {
  if (days == null) return 'unknown';
  if (days === 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

type Props = {
  onClose: () => void;
  /** Fired after a successful press so the users list can refresh its badges. */
  onReissued: (count: number) => void;
};

export default function ChaseInvitesModal({ onClose, onReissued }: Props) {
  const [includeValid, setIncludeValid] = useState(false);
  /** Ids the caller has explicitly unticked. Empty = take the server's picks. */
  const [deselected, setDeselected] = useState<Set<number>>(new Set());

  const [preview, setPreview] = useState<PendingInvitesResponse | null>(null);
  const [result, setResult] = useState<BulkInviteResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    // Once the links are on screen they are unrecoverable, so stop refetching:
    // a re-render that replaced this screen would destroy them.
    if (result) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    fetchPendingInvites({ include_valid: includeValid }, ctrl.signal)
      .then((res) => {
        setPreview(res);
        setError(null);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setError(chaseError(err, 'Could not check for pending invites.'));
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [includeValid, result]);

  const items: PendingInviteItem[] = preview?.proposal.items ?? [];

  const selectedIds = useMemo(
    () => items.map((i) => i.user_id).filter((id) => !deselected.has(id)),
    [items, deselected],
  );

  /** How many of the picks are about to have a WORKING link taken away. */
  const liveAtRisk = useMemo(
    () =>
      items.filter((i) => i.link_state === 'live' && !deselected.has(i.user_id))
        .length,
    [items, deselected],
  );

  const toggle = useCallback((id: number) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCommit = useCallback(async () => {
    if (!selectedIds.length) return;
    setCommitting(true);
    setError(null);
    try {
      const res = await bulkReissueInvites({
        user_ids: selectedIds,
        include_valid: includeValid,
      });
      setResult(res);
      onReissued(res.reissued);
    } catch (err) {
      setError(chaseError(err, 'Could not issue the invites.'));
    } finally {
      setCommitting(false);
    }
  }, [selectedIds, includeValid, onReissued]);

  const nothingToDo = preview !== null && items.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50">
      <div className="flex h-full w-full max-w-xl flex-col bg-bg-raised shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border-subtle p-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base font-semibold text-fg-primary">
              <MailWarning size={18} /> Chase the unredeemed invites
            </h2>
            <p className="mt-1 text-sm text-fg-secondary">
              {result
                ? 'Send each person their link. This is the only time they are shown.'
                : 'Issues a fresh link for everyone who was invited and never signed in.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-fg-muted hover:bg-bg-base hover:text-fg-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-status-danger/40 bg-status-danger/10 p-3 text-sm text-status-danger">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* ------------------------------------------------------------- */}
          {/* Result — the links, shown once.                               */}
          {/* ------------------------------------------------------------- */}
          {result ? (
            <>
              <div className="rounded-md border border-status-warning/40 bg-status-warning/10 p-3 text-sm text-fg-secondary">
                <strong className="text-fg-primary">
                  {n(result.reissued)} link{result.reissued === 1 ? '' : 's'} issued.
                </strong>{' '}
                There is no mailer — send each one yourself. This is the only
                time they are shown, each works once, and any earlier link for
                these people is now dead.
              </div>
              {/* The same renderer the campaign-launch success screen uses. */}
              <InviteLinkList invites={result.invites} />
            </>
          ) : (
            <>
              {preview && (
                <ProposalLedger
                  proposal={preview.proposal}
                  noun="unclaimed accounts"
                  plannedVerb="will get a fresh link"
                  cappedNote={`Only the ${n(preview.proposal.planned)} longest-waiting are in this batch. Press again for the next one.`}
                />
              )}

              {/* --------------------------------------------------------- */}
              {/* The opt-in. Second-press safety, said out loud.            */}
              {/* --------------------------------------------------------- */}
              <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border-subtle bg-bg-base p-3">
                <input
                  type="checkbox"
                  checked={includeValid}
                  onChange={(e) => {
                    setIncludeValid(e.target.checked);
                    // The population changes underneath, so old unticks would
                    // silently apply to rows the caller never saw.
                    setDeselected(new Set());
                  }}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
                />
                <span className="text-sm text-fg-secondary">
                  <span className="font-semibold text-fg-primary">
                    Also re-issue links that still work
                  </span>
                  <span className="mt-0.5 block text-xs text-fg-muted">
                    A new link kills the old one. If you already handed someone
                    their link, ticking this turns it into a dead link and they
                    will not know until they try it. Left off, this button only
                    touches people whose link has expired or was never issued.
                  </span>
                </span>
              </label>

              {nothingToDo && (
                <div className="rounded-md border border-border-subtle bg-bg-base p-3 text-sm text-fg-secondary">
                  {includeValid
                    ? 'Nobody to chase — every unclaimed account has been dealt with.'
                    : 'Nobody to chase. Anyone still unclaimed has a link that works; tick the box above to replace those too.'}
                </div>
              )}

              {items.length > 0 && (
                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                    Untick anyone you would rather chase by hand
                  </h3>
                  <div className="space-y-1">
                    {items.map((item) => {
                      const checked = !deselected.has(item.user_id);
                      return (
                        <label
                          key={item.user_id}
                          className="flex cursor-pointer items-start gap-3 rounded border border-border-subtle bg-bg-base px-3 py-2"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(item.user_id)}
                            className="mt-1 shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-fg-primary">
                              {item.full_name || item.email}
                            </div>
                            <div className="truncate text-xs text-fg-muted">
                              {item.email} &middot; {item.role.replace(/_/g, ' ')}
                            </div>
                          </div>
                          <div className="shrink-0 text-right text-xs">
                            <div className="tabular-nums text-fg-secondary">
                              waiting {waited(item.days_waiting)}
                            </div>
                            <div
                              className={
                                item.link_state === 'live'
                                  ? 'text-status-warning'
                                  : 'text-fg-muted'
                              }
                            >
                              {item.link_state === 'live'
                                ? 'link still works'
                                : item.link_state === 'expired'
                                  ? 'link expired'
                                  : 'no link'}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="space-y-2 border-t border-border-subtle p-4">
          {result ? (
            <div className="flex items-center justify-between gap-3">
              <InviteCsvButton
                invites={result.invites}
                filename="unredeemed-invites.csv"
              />
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {liveAtRisk > 0 && (
                <p className="flex items-start gap-2 text-xs text-status-warning">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    {n(liveAtRisk)} of these{' '}
                    {liveAtRisk === 1 ? 'has a link that' : 'have links that'}{' '}
                    still {liveAtRisk === 1 ? 'works' : 'work'}. Pressing now
                    kills {liveAtRisk === 1 ? 'it' : 'them'}.
                  </span>
                </p>
              )}
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-fg-secondary">
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Checking…
                    </span>
                  ) : (
                    `${n(selectedIds.length)} selected`
                  )}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-border-subtle px-3 py-2 text-sm text-fg-primary hover:bg-bg-base"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCommit}
                    disabled={loading || committing || selectedIds.length === 0}
                    className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {committing && <Loader2 size={14} className="animate-spin" />}
                    Issue {selectedIds.length > 0 ? n(selectedIds.length) : ''} link
                    {selectedIds.length === 1 ? '' : 's'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
